import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import Spinners from '../../../components/spinner';
import {
  DynamicTable,
  useDynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import BrokerSellItemsHeader from '../../../constants/table-headers/brokerSellItemsHeader';
import { axiosInstance } from '../../../services/apiInterceptor';
import {
  useBoolean,
  useGetCsSellItems,
  useSubmitBrokerAssist,
  useGetOptionExerciseSettlementType,
  useGetCsBookingTypes,
} from '../../../hooks';
import { CsBrokerTradeToolService } from '../../../services/wealthlane-award-services/services/CsBrokerTradeToolService';
import { OptionExerciseSharedService } from '../../../services/wealthlane-award-services/services/OptionExerciseSharedService';
import type { FillTradeOrderDto } from '../../../services/wealthlane-award-settlement-services/services/CsBrokerTradeToolSettlementService';
import { CsBrokerTradeToolSettlementService } from '../../../services/wealthlane-award-settlement-services/services/CsBrokerTradeToolSettlementService';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import type { ParticipantSearchResultDto } from '../../../services/wealthlane-customer-services/services/CustomerSuccessService';
import DoneTradeModal from './doneTradeModal';
import ViewExerciseModal from './viewExerciseModal';
import AlertPopup from '../../../components/alert-popup';
import { Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto } from '@/services/wealthlane-award-services/models/Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto';
import { Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSubmitRowDto } from '@/services/wealthlane-award-services/models/Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSubmitRowDto';
import { Wealthlane_AwardService_Dtos_BrokerTradeTool_CsDoneTradePreviewDto } from '@/services/wealthlane-award-services/models/Wealthlane_AwardService_Dtos_BrokerTradeTool_CsDoneTradePreviewDto';

interface RowState {
  sellShares: number;
  bookingType: string;
  settlementTypeId: string;
  error: string;
  calcExResult?: Record<string, unknown> | null;
  isCalcLoading?: boolean;
}

interface SellShareSectionProps {
  participantId: string | undefined;
  companyId: string | undefined;
  participant: ParticipantSearchResultDto | null;
  reasonCode: string;
  setReasonCodeError?: (err: string) => void;
  awardHandleRequest: (promise: any) => Promise<any>;
  settlementHandleRequest: (promise: any) => Promise<any>;
  orderStatusOptions: any;
  isOrderStatusLoading: boolean;
  canSubmitBA: boolean;
  canFillDT: boolean;
  /** Defaults to BrokerAssist. Use DoneTrade for the Done Trade settings page. */
  defaultBookingType?: 'BrokerAssist' | 'DoneTrade';
  /** When false, skip reason-code UI validation (caller still supplies reasonCode for the API). */
  requireReasonCode?: boolean;
  /** Label shown in the Booking Type column. */
  bookingTypeLabel?: string;
}

function SellShareSection({
  participantId,
  companyId,
  participant,
  reasonCode,
  setReasonCodeError,
  awardHandleRequest,
  settlementHandleRequest,
  orderStatusOptions,
  isOrderStatusLoading,
  canSubmitBA,
  canFillDT,
  defaultBookingType = 'BrokerAssist',
  requireReasonCode = true,
  bookingTypeLabel = 'Broker Assist',
}: SellShareSectionProps) {
  const queryClient = useQueryClient();
  const { tableInfo } = useDynamicTable();

  const {
    schemaData: sellSchemaData,
    isTableSchemaLoading,
    status: schemaStatus,
  } = useTableSchemaLoader({
    identifier: 'CS_BROKER_SELL_ITEMS_TABLE',
  });
  const hasSellSchema = schemaStatus === 'success' && sellSchemaData?.columns?.length > 0;

  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [doneTradePreview, setDoneTradePreview] =
    useState<Wealthlane_AwardService_Dtos_BrokerTradeTool_CsDoneTradePreviewDto | null>(null);
  const [doneTradeSubmitDto, setDoneTradeSubmitDto] =
    useState<Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSubmitRowDto | null>(null);
  const [isDoneTradeSubmitting, setIsDoneTradeSubmitting] = useState(false);
  const [doneTradeStep, setDoneTradeStep] = useState<
    'idle' | 'submitted' | 'order-created' | 'filled'
  >('idle');
  const [pendingTradeOrderId, setPendingTradeOrderId] = useState<string | null>(null);
  const [pendingTransactionId, setPendingTransactionId] = useState<string | null>(null);
  const [submittingItemId, setSubmittingItemId] = useState<string | null>(null);
  const [calculatingItemId, setCalculatingItemId] = useState<string | null>(null);

  const {
    value: isDoneTradeOpen,
    setFalse: closeDoneTrade,
    setTrue: openDoneTrade,
  } = useBoolean(false);
  const {
    value: isViewExerciseOpen,
    setFalse: closeViewExercise,
    setTrue: openViewExercise,
  } = useBoolean(false);
  const [viewExerciseData, setViewExerciseData] = useState<any>(null);
  const [viewExerciseRow, setViewExerciseRow] = useState<any>(null);
  const { value: isConfirmOpen, setFalse: closeConfirm, setTrue: openConfirm } = useBoolean(false);
  const [pendingActionItem, setPendingActionItem] =
    useState<Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto | null>(null);

  const { data: settlementTypeOptions, isLoading: isSettlementTypesLoading } =
    useGetOptionExerciseSettlementType(awardHandleRequest);
  const { data: bookingTypeOptions, isLoading: isBookingTypesLoading } =
    useGetCsBookingTypes(awardHandleRequest);

  const sellSkipCount = tableInfo?.skipCount ?? 0;
  const sellMaxResult = tableInfo?.maxResultCount ?? 10;

  const sellFilters = useMemo(
    () => ({
      recordTypeName: tableInfo?.recordTypeName,
      awardType: tableInfo?.awardType,
    }),
    [tableInfo?.recordTypeName, tableInfo?.awardType]
  );

  const { data: sellItemsData, isLoading: isSellItemsLoading } = useGetCsSellItems(
    awardHandleRequest,
    participantId,
    companyId,
    sellSkipCount,
    sellMaxResult,
    sellFilters
  );
  const sellItems = sellItemsData?.items;
  const sellTotalCount = sellItemsData?.totalCount ?? 0;

  const { mutate: submitBA, isLoading: isSubmittingBA } = useSubmitBrokerAssist(awardHandleRequest);

  useEffect(() => {
    if (sellItems) {
      const initial: Record<string, RowState> = {};
      sellItems.forEach((item) => {
        if (!rowStates[item.itemId]) {
          initial[item.itemId] = {
            sellShares: 0,
            bookingType: defaultBookingType,
            settlementTypeId: '',
            error: '',
          };
        }
      });
      if (Object.keys(initial).length > 0) {
        setRowStates((prev) => ({ ...initial, ...prev }));
      }
    }
  }, [sellItems, defaultBookingType]);

  const validateRow = useCallback(
    (item: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto, row: RowState): string => {
      if (row.sellShares <= 0) return 'Enter shares to sell';
      if (row.sellShares > item.maxShares) return `Cannot exceed ${item.maxShares} shares`;
      if (
        item.itemType === 'OptionExercise' &&
        (!row.settlementTypeId || row.settlementTypeId === '')
      )
        return 'Select a Settlement Type';
      return '';
    },
    []
  );

  const buildSubmitDto = useCallback(
    (
      item: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto,
      row: RowState
    ): Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSubmitRowDto => {
      return {
        participantId: participant!.participantId,
        reasonCode,
        itemId: item.itemId,
        itemType: item.itemType,
        sellShares: row.sellShares,
        bookingType: row.bookingType === 'BrokerAssist' ? 'BrokerAssisted' : 'DoneTrade',
        settlementTypeId: item.itemType === 'OptionExercise' ? row.settlementTypeId : undefined,
        companyId: companyId || undefined,
      };
    },
    [participant, reasonCode, companyId]
  );

  const handleAction = useCallback(
    (item: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto) => {
      const row = rowStates[item.itemId];
      if (!row) return;

      if (requireReasonCode && reasonCode === '') {
        setReasonCodeError?.('Select a Reason Code');
        return;
      }

      const error = validateRow(item, row);
      if (error !== '') {
        setRowStates((prev) => ({
          ...prev,
          [item.itemId]: { ...prev[item.itemId], error },
        }));
        return;
      }

      if (row.sellShares <= 0) {
        setRowStates((prev) => ({
          ...prev,
          [item.itemId]: { ...prev[item.itemId], error: 'No changes to submit' },
        }));
        return;
      }

      setPendingActionItem(item);
      openConfirm();
    },
    [rowStates, reasonCode, requireReasonCode, validateRow, setReasonCodeError, openConfirm]
  );

  const handleConfirmSubmit = useCallback(() => {
    if (!pendingActionItem) return;

    const item = pendingActionItem;
    const row = rowStates[item.itemId];
    if (!row) return;

    const dto = buildSubmitDto(item, row);

    closeConfirm();
    setPendingActionItem(null);

    if (row.bookingType === 'BrokerAssist') {
      setSubmittingItemId(item.itemId);
      submitBA(dto, {
        onSuccess: () => {
          setRowStates((prev) => ({
            ...prev,
            [item.itemId]: {
              sellShares: 0,
              bookingType: defaultBookingType,
              settlementTypeId: '',
              error: '',
            },
          }));
        },
        onSettled: () => {
          setSubmittingItemId(null);
        },
      });
    } else {
      const preview: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsDoneTradePreviewDto = {
        tradeOrderId: '',
        transactionId: '',
        createdDate: new Date().toISOString(),
        orderType: 'Market',
        company: participant?.companyName ?? '',
        ticker: participant?.ticker ?? '',
        shares: row.sellShares,
        brokerSellType: 'Shr',
        brokerName: participant?.brokerName ?? '',
        brokerAccount: participant?.brokerAccountNumber ?? '',
        orderStatus: 'Filled',
        awardType: item.awardType,
        recordType: item.itemType,
      };
      setDoneTradePreview(preview);
      setDoneTradeSubmitDto(dto);
      setSubmittingItemId(item.itemId);
      openDoneTrade();
    }
  }, [
    pendingActionItem,
    rowStates,
    buildSubmitDto,
    closeConfirm,
    submitBA,
    participant,
    openDoneTrade,
    defaultBookingType,
  ]);

  const handleDoneTradeSubmit = useCallback(
    async (fillData: FillTradeOrderDto) => {
      if (!doneTradeSubmitDto) return;
      setIsDoneTradeSubmitting(true);
      try {
        let transactionId = pendingTransactionId;
        let tradeOrderId = pendingTradeOrderId;

        if (!transactionId) {
          const submitResult = await awardHandleRequest(
            CsBrokerTradeToolService.postApiAwardApiAppCsBrokerTradeToolSubmit(doneTradeSubmitDto)
          );
          if (!submitResult?.transactionId) throw new Error('Failed to create transaction');
          transactionId = submitResult.transactionId;
          setPendingTransactionId(transactionId);
          setDoneTradeStep('submitted');
        }

        if (!tradeOrderId) {
          try {
            const orderId = await settlementHandleRequest(
              CsBrokerTradeToolSettlementService.postApiAwardSettlementApiAppCsBrokerTradeToolSettlementCreateOrder(
                transactionId,
                companyId || undefined,
                'DoneTrade'
              )
            );
            if (!orderId) throw new Error('Failed to create trade order');
            tradeOrderId = orderId;
            setPendingTradeOrderId(tradeOrderId);
            setDoneTradeStep('order-created');
          } catch (err) {
            toast.error(
              <ToastComponent
                label='Error'
                message='Transaction was created but trade order creation failed. Please retry.'
              />
            );
            throw err;
          }
        }

        try {
          await settlementHandleRequest(
            CsBrokerTradeToolSettlementService.putApiAwardSettlementApiAppCsBrokerTradeToolSettlementFill(
              tradeOrderId,
              companyId || undefined,
              fillData
            )
          );
        } catch (err) {
          toast.error(
            <ToastComponent
              label='Error'
              message='Trade order was created but fill failed. Please retry or contact support.'
            />
          );
          throw err;
        }

        setDoneTradeStep('filled');
        setPendingTransactionId(null);
        setPendingTradeOrderId(null);
        toast.success(
          <ToastComponent label='Success' message='Done trade completed successfully.' />
        );
        void queryClient.invalidateQueries('getCsTransactionHistory');
        void queryClient.invalidateQueries('getCsSellItems');
        const itemId = doneTradeSubmitDto.itemId;
        closeDoneTrade();
        setDoneTradePreview(null);
        setDoneTradeSubmitDto(null);
        setDoneTradeStep('idle');
        setRowStates((prev) => ({
          ...prev,
          [itemId]: {
            sellShares: 0,
            bookingType: defaultBookingType,
            settlementTypeId: '',
            error: '',
          },
        }));
      } catch (err: any) {
        const msg = err?.body?.error?.message || err?.message || '';
        if (msg.includes('STALE_BALANCE')) {
          toast.error(
            <ToastComponent
              label='Error'
              message='Balance has changed. Refreshing available shares...'
            />
          );
          void queryClient.invalidateQueries('getCsSellItems');
        }
      } finally {
        setIsDoneTradeSubmitting(false);
        setSubmittingItemId(null);
      }
    },
    [
      doneTradeSubmitDto,
      awardHandleRequest,
      settlementHandleRequest,
      queryClient,
      closeDoneTrade,
      pendingTransactionId,
      pendingTradeOrderId,
      companyId,
      defaultBookingType,
    ]
  );

  const handleCalculateExercise = useCallback(
    async (item: any) => {
      const row = rowStates[item.itemId];
      if (!row || !row.sellShares || row.sellShares <= 0) {
        toast.error(
          <ToastComponent label='Error' message='Enter sell shares before calculating.' />
        );
        return;
      }
      setCalculatingItemId(item.itemId);
      try {
        const result = await awardHandleRequest(
          OptionExerciseSharedService.postApiAwardApiAppOptionExerciseSharedCalculateExerciseForVendor(
            companyId || undefined,
            participant?.participantId || undefined,
            {
              awardId: item.grantId || item.itemId,
              exerciseShare: row.sellShares,
              exerciseType: row.settlementTypeId || 'SellAll',
              settlementTypeId: row.settlementTypeId || undefined,
            }
          )
        );
        if (!result) return;
        setViewExerciseData(result);
        setViewExerciseRow(item);
        openViewExercise();
      } catch {
        // handleRequest already shows error toasts
      } finally {
        setCalculatingItemId(null);
      }
    },
    [rowStates, participant, awardHandleRequest, openViewExercise, companyId]
  );

  const handleViewExercise = useCallback(
    (item: any) => {
      const row = rowStates[item.itemId];
      if (!row || !row.calcExResult) return;
      setViewExerciseData(row.calcExResult);
      setViewExerciseRow(item);
      openViewExercise();
    },
    [rowStates, openViewExercise]
  );

  const autoCalculateExercise = useCallback(
    async (
      item: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto,
      shares: number,
      settlementTypeId: string
    ) => {
      if (!shares || shares <= 0 || !settlementTypeId) {
        setRowStates((prev) => ({
          ...prev,
          [item.itemId]: { ...prev[item.itemId], calcExResult: null, isCalcLoading: false },
        }));
        return;
      }
      setRowStates((prev) => ({
        ...prev,
        [item.itemId]: { ...prev[item.itemId], isCalcLoading: true },
      }));
      try {
        const result = await awardHandleRequest(
          OptionExerciseSharedService.postApiAwardApiAppOptionExerciseSharedCalculateExerciseForVendor(
            companyId || undefined,
            participant?.participantId || undefined,
            {
              awardId: item.grantId || item.itemId,
              exerciseShare: shares,
              exerciseType: settlementTypeId || 'SellAll',
              settlementTypeId: settlementTypeId || undefined,
            }
          )
        );
        setRowStates((prev) => ({
          ...prev,
          [item.itemId]: {
            ...prev[item.itemId],
            calcExResult: result ?? null,
            isCalcLoading: false,
          },
        }));
      } catch {
        setRowStates((prev) => ({
          ...prev,
          [item.itemId]: { ...prev[item.itemId], calcExResult: null, isCalcLoading: false },
        }));
      }
    },
    [participant, awardHandleRequest, companyId]
  );

  const updateRowState = useCallback(
    (itemId: string, field: string, value: string | number) => {
      setRowStates((prev) => ({
        ...prev,
        [itemId]: { ...prev[itemId], [field]: value, error: '' },
      }));

      const item = sellItems?.find((i) => i.itemId === itemId);
      if (
        item?.recordTypeName === 'Option Intent' &&
        (field === 'sellShares' || field === 'settlementTypeId')
      ) {
        const currentRow = rowStates[itemId];
        const shares = field === 'sellShares' ? (value as number) : currentRow?.sellShares ?? 0;
        const settlement =
          field === 'settlementTypeId' ? (value as string) : currentRow?.settlementTypeId ?? '';
        void autoCalculateExercise(item, shares, settlement);
      }
    },
    [sellItems, rowStates, autoCalculateExercise]
  );

  const tableData = useMemo(() => {
    if (!sellItems) return [];
    return sellItems.map((item) => {
      const row = rowStates[item.itemId];
      const sellShares = row?.sellShares ?? 0;
      return {
        ...item,
        sellShares,
        bookingType: row?.bookingType ?? defaultBookingType,
        settlementTypeId: row?.settlementTypeId ?? '',
        estimatedValue: sellShares * (item.referenceMarketPrice ?? 0),
        rowError: row?.error ?? '',
        calcExResult: row?.calcExResult ?? null,
        isCalcLoading: row?.isCalcLoading ?? false,
      };
    });
  }, [sellItems, rowStates, defaultBookingType]);

  const sellSchema = useMemo(
    () => ({
      columns: BrokerSellItemsHeader({
        actions: sellSchemaData?.columns || [],
        bookingTypeOptions: bookingTypeOptions ?? [],
        isBookingTypesLoading,
        settlementTypeOptions: settlementTypeOptions ?? [],
        isSettlementTypesLoading,
        bookingTypeLabel,
        onAction: (rowData: any) => {
          const item = sellItems?.find((i) => i.itemId === rowData.itemId);
          if (item) handleAction(item);
        },
        onCalcExercise: (rowData: any) => {
          const item = sellItems?.find((i) => i.itemId === rowData.itemId);
          if (item) handleCalculateExercise(item);
        },
        onViewExercise: (rowData: any) => {
          const item = sellItems?.find((i) => i.itemId === rowData.itemId);
          if (item) handleViewExercise(item);
        },
        onUpdateRow: updateRowState,
        submittingItemId,
        calculatingItemId,
        canSubmitBA,
        canFillDT,
      }),
      options: sellSchemaData?.options as any,
    }),
    [
      sellSchemaData,
      bookingTypeOptions,
      isBookingTypesLoading,
      settlementTypeOptions,
      isSettlementTypesLoading,
      bookingTypeLabel,
      sellItems,
      handleAction,
      handleCalculateExercise,
      handleViewExercise,
      updateRowState,
      submittingItemId,
      calculatingItemId,
      canSubmitBA,
      canFillDT,
    ]
  );

  return (
    <>
      {isTableSchemaLoading ? (
        <Spinners id='sellSchemaSpinner' />
      ) : !hasSellSchema ? (
        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
          Table schema not available. Please seed the <strong>CS_BROKER_SELL_ITEMS_TABLE</strong>{' '}
          schema in the Administration Service.
        </div>
      ) : (
        <DynamicTable
          id='brokerSellItemsTable'
          schema={sellSchema}
          data={tableData}
          totalCounts={sellTotalCount || 10}
          loading={isSellItemsLoading ? 'loading' : 'success'}
          showPagination={sellTotalCount > 0}
          showPaginationList={sellTotalCount > sellMaxResult}
          components={{}}
        />
      )}

      {/* Submit Confirmation */}
      <AnimationWrapper>
        {isConfirmOpen && (
          <div id='submitConfirmModal'>
            <AlertPopup
              isShow={isConfirmOpen}
              handleSubmit={handleConfirmSubmit}
              closeModal={() => {
                closeConfirm();
                setPendingActionItem(null);
              }}
              customTitle='Are you sure you want to submit the changes?'
              loading={isSubmittingBA}
            />
          </div>
        )}
      </AnimationWrapper>

      {/* Done Trade Modal */}
      <AnimationWrapper>
        {isDoneTradeOpen && (
          <DoneTradeModal
            isOpen={isDoneTradeOpen}
            onClose={() => {
              closeDoneTrade();
              setDoneTradePreview(null);
              setDoneTradeSubmitDto(null);
              setPendingTransactionId(null);
              setPendingTradeOrderId(null);
              setDoneTradeStep('idle');
              setSubmittingItemId(null);
            }}
            previewData={doneTradePreview}
            onSubmit={handleDoneTradeSubmit}
            isSubmitting={isDoneTradeSubmitting}
            orderStatusOptions={orderStatusOptions}
            isOrderStatusLoading={isOrderStatusLoading}
            partialFailureStep={doneTradeStep}
          />
        )}
      </AnimationWrapper>

      {/* View Exercise Modal (manual calculate + Option Intent view) */}
      <AnimationWrapper>
        {isViewExerciseOpen && viewExerciseData && (
          <ViewExerciseModal
            isModalOpen={isViewExerciseOpen}
            closeModal={() => {
              closeViewExercise();
              setViewExerciseData(null);
              setViewExerciseRow(null);
            }}
            exerciseResponse={viewExerciseData}
            row={viewExerciseRow}
          />
        )}
      </AnimationWrapper>
    </>
  );
}

export default withDynamicTableContext(SellShareSection, true, axiosInstance);
